<template>
  <BaseWidget
    :deviceIds="deviceIds"
    :widgetId="widgetId"
    :widgetType="widgetType"
    :widgetMaxDevices="widgetMaxDevices"
    :title="name"
    :subtitle="deviceType"
    :class="[sizeClass]"
    :fullscreen="fullscreen"
    :observeSize="!isDialog"
    :isDialog="isDialog"
    :isGrayOut="isWaitingInitialData(deviceId)"
    @close="close"
    @removeDevice="removeDevice"
    @removeWidget="removeWidget"
    @widgetSizeChanged="updateWidgetSize"
    @refreshDashboard="refreshDashboard"
    data-cy="currentReadingsWidget"
  >
    <template v-slot:toolbar>
      <PopoverToolbar :deviceId="deviceId" />
    </template>
    <template v-slot:left-toolbar>
      <div class="toolbar">
        <v-btn variant="text" @click="refresh" data-cy="refreshPopover">
          <v-icon icon="mdi-refresh" color="grey" />
        </v-btn>
      </div>
    </template>
    <template v-slot:content>
      <PopoverMainContent
        :deviceId="deviceId"
        :permissions="permissions"
        :isWaitingInitialData="isWaitingInitialData(deviceId)"
        ref="popoverMain"
      />
    </template>
  </BaseWidget>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { useStore } from 'vuex'
import throttle from 'lodash/throttle'
import BaseWidget from '@/components/widgets/BaseWidget.vue'
import PopoverToolbar from '@/components/devicePopover/content/title/popoverToolbar.vue'
import PopoverMainContent from '@/components/devicePopover/content/popoverMainContent.vue'
import { deviceModelDisplay } from '@/services/map'
import { getPermissionsByDeviceId } from '@/services/subscriptions'
import { THROTTLE_INTERVAL } from '@/services/device-metadata'
import { useWaitingData } from '@/composables/useWaitingData'
import { useStreaming } from '@/composables/useStreaming'
import { NAMESPACES, type RootState } from '@/store'
import { DEVICE_GETTERS, DEVICE_ACTIONS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import type { WidgetSettings } from '@/types/dashboard'
import type { WidgetComponentKey } from '@/views/dashboard.vue'

const BREAKPOINT_WIDTH_XL = 700
const BREAKPOINT_WIDTH_LG = 600
const BREAKPOINT_WIDTH_MD = 500
const BREAKPOINT_WIDTH_SM = 400
const BREAKPOINT_WIDTH_XS = 340
const BREAKPOINT_WIDTH_XXS = 280

interface Dimensions {
  width: number
  height: number
}

type PopoverMainRef = InstanceType<typeof PopoverMainContent>

export default defineComponent({
  name: 'PopoverWidget',
  components: {
    BaseWidget,
    PopoverToolbar,
    PopoverMainContent
  },
  setup() {
    const store = useStore<RootState>()
    const { isWaitingInitialData } = useWaitingData()
    const { enableTelemetryStreaming, openTelemetryStream, closeTelemetryStream } = useStreaming()

    function getLatestTelemetryAndUpdate(deviceId: string): Promise<void> {
      return store.dispatch(
        `${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.GET_LATEST_TELEMETRY_AND_UPDATE}`,
        deviceId
      )
    }

    return {
      isWaitingInitialData,
      enableTelemetryStreaming,
      openTelemetryStream,
      closeTelemetryStream,
      getLatestTelemetryAndUpdate
    }
  },
  props: {
    widgetId: {
      type: String,
      default: undefined
    },
    widgetType: {
      type: String as PropType<WidgetComponentKey>,
      default: undefined
    },
    widgetMaxDevices: {
      type: Object as PropType<Record<WidgetComponentKey, number>>,
      default: undefined
    },
    deviceIds: {
      type: Array as PropType<string[]>,
      default: (): string[] => []
    },
    width: {
      type: Number,
      default: undefined
    },
    height: {
      type: Number,
      default: undefined
    },
    settings: {
      type: Object as PropType<WidgetSettings>,
      default: undefined
    },
    isDialog: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'removeWidget', 'refreshDashboard'],
  data() {
    return {
      fullscreen: false,
      dimensions: { width: 0, height: 0 } as Dimensions,
      throttledRefresh: null as ReturnType<typeof throttle> | null
    }
  },
  computed: {
    deviceId(): string {
      return this.deviceIds?.[0] ?? ''
    },
    name(): string {
      return this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`](
        this.deviceId
      )
    },
    deviceModel(): string {
      return this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`](
        this.deviceId
      )
    },
    deviceType(): string {
      const modelKey = this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_KEY}`
      ](this.deviceModel)
      return deviceModelDisplay(modelKey, this.$t.bind(this))
    },
    sizeClass(): string {
      if (this.isDialog) return 'widget-md'
      let className = ''
      if (this.dimensions.width <= BREAKPOINT_WIDTH_XL) className = 'widget-xl'
      if (this.dimensions.width <= BREAKPOINT_WIDTH_LG) className = 'widget-lg'
      if (this.dimensions.width <= BREAKPOINT_WIDTH_MD) className = 'widget-md'
      if (this.dimensions.width <= BREAKPOINT_WIDTH_SM) className = 'widget-sm'
      if (this.dimensions.width <= BREAKPOINT_WIDTH_XS) className = 'widget-xs'
      if (this.dimensions.width <= BREAKPOINT_WIDTH_XXS) className = 'widget-xxs'
      return className
    },
    permissions(): string[] {
      return getPermissionsByDeviceId(this.deviceId)
    }
  },
  async mounted() {
    const main = this.$refs.popoverMain as PopoverMainRef
    this.throttledRefresh = throttle(main.refresh.bind(main), THROTTLE_INTERVAL)
    if (this.enableTelemetryStreaming) {
      await this.openTelemetryStream(this.deviceId, this.throttledRefresh)
    }
  },
  unmounted() {
    this.closeTelemetryStream()
    this.throttledRefresh?.cancel()
  },
  methods: {
    close(): void {
      this.$emit('close')
    },
    removeDevice(_index: number, options: unknown): void {
      this.removeWidget(options)
    },
    removeWidget(options: unknown): void {
      this.$emit('removeWidget', options)
    },
    refreshDashboard(): void {
      this.$emit('refreshDashboard')
    },
    refresh(): void {
      this.getLatestTelemetryAndUpdate(this.deviceId)
      const main = this.$refs.popoverMain as PopoverMainRef
      main?.refresh()
    },
    updateWidgetSize(dims: Dimensions): void {
      this.dimensions = dims
    }
  }
})
</script>
<style lang="scss" scoped>
.toolbar {
  padding-left: 6px;
  .v-btn {
    min-width: 10px !important;
    padding: 0 0.4rem !important;
  }
}
</style>
<style lang="scss">
.widget-xxs,
.widget-xs {
  .widget-content-container {
    padding: 4px;
  }
}
</style>
